import { useMemo } from "react";

import { LoadingSpinner } from "~/shared/components/loading-spinner/loading-spinner.tsx";
import { TrackingMap } from "~/shared/components/tracking-map/tracking-map.tsx";
import { buildOrderedMapPings } from "~/shared/utils/map-building-utils.ts";
import {
	formatDistanceToNextStop,
	getTrackingStep,
} from "~/shared/utils/tracking-status.ts";

import { useGetPublicTracking } from "./hooks/use-get-public-tracking.ts";
import { ShipmentDetails } from "./shipment-details/shipment-details.tsx";
import { ShipmentTracking } from "./shipment-tracking/shipment-tracking.tsx";
import {
	buildStopPings,
	buildTrackingPings,
	findNextStop,
	findTerminalStops,
} from "./utils/shipment-map-utils.ts";

type ShipmentResultsProps = {
	readonly poNumber: number;
};

export function ShipmentResults({ poNumber }: ShipmentResultsProps) {
	const {
		trackingUpdates,
		stops,
		mode,
		status,
		message,
		proNumber,
		pickedUpDate,
		deliveredDate,
		isLoading,
		isFetching,
		error,
		errorMessage,
	} = useGetPublicTracking(poNumber);

	const sortedUpdates = useMemo(
		() =>
			[...(trackingUpdates ?? [])].sort(
				(a, b) => Date.parse(b.sortDate || "") - Date.parse(a.sortDate || ""),
			),
		[trackingUpdates],
	);

	const stopPings = useMemo(() => buildStopPings(stops), [stops]);

	const trackingPings = useMemo(
		() => buildTrackingPings(sortedUpdates),
		[sortedUpdates],
	);

	const mapPings = useMemo(
		() => buildOrderedMapPings(trackingPings, stopPings),
		[trackingPings, stopPings],
	);

	const { firstPickup, finalDrop } = useMemo(
		() => findTerminalStops(stops),
		[stops],
	);

	const nextAppointment = useMemo(() => findNextStop(stops), [stops]);

	const isLtl = mode?.toUpperCase() === "LTL";
	const isDelivered = getTrackingStep(status) === "Delivered";

	const distanceToNextStop = useMemo(() => {
		if (isLtl || isDelivered) return null;
		return formatDistanceToNextStop(sortedUpdates[0]?.distance);
	}, [isLtl, isDelivered, sortedUpdates]);

	if (error) {
		return (
			<p
				data-testid={"shipment-results-error"}
				style={{ color: "red", fontStyle: "italic", fontWeight: "bold" }}
			>
				{errorMessage}
			</p>
		);
	}

	if (message) {
		return (
			<p
				data-testid={"shipment-results-message"}
				style={{ color: "red", fontStyle: "italic", fontWeight: "bold" }}
			>
				{message}
			</p>
		);
	}

	if (isLoading || isFetching) {
		return <LoadingSpinner isVisible />;
	}

	return (
		<>
			<div>
				<ShipmentDetails
					deliveryDate={deliveredDate ?? finalDrop?.stopDate ?? null}
					finalDrop={finalDrop?.location ?? null}
					firstPickup={firstPickup?.location ?? null}
					latestStatus={status}
					mode={mode}
					nextAppointment={nextAppointment}
					poNumber={poNumber}
					proNumber={proNumber}
					receiver={finalDrop?.locationWithZip ?? null}
					shipDate={pickedUpDate ?? firstPickup?.stopDate ?? null}
					shipper={firstPickup?.locationWithZip ?? null}
				/>
				<ShipmentTracking
					distanceToNextStop={distanceToNextStop}
					trackingUpdates={sortedUpdates}
				/>
			</div>
			<div>
				<TrackingMap height={"550px"} pings={mapPings} />
			</div>
		</>
	);
}
